// src/utils/formatPoints.ts
// ─────────────────────────────────────────────────────────────────────────────
//  Jednotné zaokrouhlování a formátování bodů / skóre.
//
//  Problém: součty desetinných čísel v JS dávají float šum
//  (90.1 + 55 + 90 + 125.3 + 15 + 60.2 = 435.59999999999997).
//  Tyto helpery se používají:
//   - při ZÁPISU do Firestore (JudgeTarget, JudgeView.finalizeResults)
//   - při ZOBRAZENÍ (UserResults, …)
// ─────────────────────────────────────────────────────────────────────────────

/** Zaokrouhlí na `decimals` desetinných míst bez float šumu (435.5999… → 435.6). */
export function roundPoints(value: number, decimals = 2): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const f = 10 ** decimals;
  return Math.round((n + Number.EPSILON) * f) / f;
}

/**
 * Formát bodů pro zobrazení: max `decimals` desetinných míst,
 * koncové nuly se ořezávají (55.0 → "55", 435.6 → "435.6").
 * Neplatná hodnota → "–".
 */
export function fmtPoints(
  value: number | string | null | undefined,
  decimals = 2
): string {
  if (value === null || value === undefined || value === "") return "–";
  const n = Number(value);
  if (!Number.isFinite(n)) return "–";
  return String(roundPoints(n, decimals));
}

/**
 * Formát s PEVNÝM počtem desetinných míst (pro procenta / průměry a sloupce
 * bodů s počtem míst z pointsDecimals): 87.4 → "87.4", 100 → "100.0".
 */
export function fmtFixed(
  value: number | string | null | undefined,
  decimals = 1
): string {
  if (value === null || value === undefined || value === "") return "–";
  const n = Number(value);
  if (!Number.isFinite(n)) return "–";
  return roundPoints(n, decimals).toFixed(decimals);
}

/**
 * Počet desetinných míst pro sloupec bodů, aby čísla stála zarovnaná pod
 * sebou: nejvyšší počet, který se v hodnotách po zaokrouhlení na `max` míst
 * skutečně vyskytuje. Body 1772.5 a 1682 → 1 („1772.5", „1682.0"), samá celá
 * čísla → 0. Prázdné a neplatné hodnoty se přeskakují. Formátovat pak
 * fmtFixed(value, decimals); fmtPoints koncové nuly ořezává, a sloupec by se
 * tak rozházel.
 */
export function pointsDecimals(
  values: ReadonlyArray<number | string | null | undefined>,
  max = 2
): number {
  let decimals = 0;
  values.forEach((value) => {
    if (value === null || value === undefined || value === "") return;
    const n = Number(value);
    if (!Number.isFinite(n)) return;
    const rounded = roundPoints(n, max);
    // Když se zaokrouhlením na `decimals` míst nezmění, víc míst nepotřebuje
    // (porovnání !== je bezpečné: obě strany jsou celé číslo / mocnina 10,
    // a stejná hodnota tak vyjde bitově stejně)
    while (decimals < max && roundPoints(rounded, decimals) !== rounded) {
      decimals += 1;
    }
  });
  return decimals;
}
